import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { academicService, adminService, fetchAllPages } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Card, Badge, Select, EmptyState, Spinner, Pagination } from '../../components/ui';

const STATUS_TONE = { present: 'green', absent: 'red', late: 'yellow', excused: 'gray' };
const PAGE_SIZE = 20;

function AdminAttendance() {
  const { addNotification } = useNotification();
  const { t } = useTranslation();

  const [modules, setModules] = useState([]);
  const [moduleId, setModuleId] = useState('');
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summaryRecords, setSummaryRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  useEffect(() => {
    academicService.getModules({ page_size: 100 }).then((res) => {
      const data = res.data;
      setModules(Array.isArray(data) ? data : data.results || []);
    }).catch(() => {});
  }, []);

  const fetchAttendance = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (moduleId) params.module = moduleId;
    adminService.getAttendance(params)
      .then((res) => {
        const data = res.data;
        setRecords(Array.isArray(data) ? data : data.results || []);
        setCount(Array.isArray(data) ? data.length : data.count || 0);
      })
      .catch(() => addNotification(t('Failed to load attendance records'), 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId, page]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);
  useEffect(() => {
    fetchAllPages('/enrollments/attendance/', moduleId ? { module: moduleId, page_size: 100 } : { page_size: 100 })
      .then(setSummaryRecords)
      .catch(() => setSummaryRecords([]));
  }, [moduleId]);

  // Per-student summary for the currently filtered records
  const summaryByStudent = {};
  summaryRecords.forEach((r) => {
    const key = r.student.id;
    if (!summaryByStudent[key]) {
      summaryByStudent[key] = { student: r.student, total: 0, present: 0, absent: 0, late: 0, excused: 0 };
    }
    summaryByStudent[key].total += 1;
    summaryByStudent[key][r.status] += 1;
  });
  const summaries = Object.values(summaryByStudent);

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={t('Attendance')} subtitle={t('Presence records across classes')} />
        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex items-center gap-3 flex-wrap">
            <Select value={moduleId} onChange={(e) => { setModuleId(e.target.value); setPage(1); }} className="sm:max-w-xs">
              <option value="">{t('All classes')}</option>
              {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
          </div>

          {loading ? (
            <div className="p-12 flex justify-center"><Spinner /></div>
          ) : records.length === 0 ? (
            <Card><EmptyState title={t('No attendance records yet')} description={t('Records appear here once a teacher takes attendance for a class session.')} /></Card>
          ) : (
            <>
              {/* Per-student summary */}
              <Card padded={false} className="overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-800 text-sm">
                  {t('Attendance Rate by Student')}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">{t('Student')}</th>
                        <th className="text-left px-4 py-3">{t('Present')}</th>
                        <th className="text-left px-4 py-3">{t('Absent')}</th>
                        <th className="text-left px-4 py-3">{t('Late')}</th>
                        <th className="text-left px-4 py-3">{t('Excused')}</th>
                        <th className="text-left px-4 py-3">{t('Attendance Rate')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {summaries.map((s) => {
                        const rate = s.total > 0 ? Math.round(((s.present + s.late) / s.total) * 100) : 0;
                        return (
                          <tr key={s.student.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{s.student.first_name} {s.student.last_name}</td>
                            <td className="px-4 py-3 text-emerald-700">{s.present}</td>
                            <td className="px-4 py-3 text-red-700">{s.absent}</td>
                            <td className="px-4 py-3 text-amber-700">{s.late}</td>
                            <td className="px-4 py-3 text-gray-500">{s.excused}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${rate >= 90 ? 'bg-emerald-500' : rate >= 75 ? 'bg-amber-500' : 'bg-red-500'}`}
                                    style={{ width: `${rate}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500">{rate}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>

              {/* Flat records */}
              <Card padded={false} className="overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 font-semibold text-gray-800 text-sm">
                  {t('All Records')}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-3">{t('Student')}</th>
                        <th className="text-left px-4 py-3">{t('Class')}</th>
                        <th className="text-left px-4 py-3">{t('Session')}</th>
                        <th className="text-left px-4 py-3">{t('Status')}</th>
                        <th className="text-left px-4 py-3">{t('Note')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {records.map((r) => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-gray-900">{r.student.first_name} {r.student.last_name}</td>
                          <td className="px-4 py-3 text-gray-600">{r.module_name}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {r.event_title}
                            <div className="text-xs text-gray-400">{new Date(r.event_start).toLocaleDateString()}</div>
                          </td>
                          <td className="px-4 py-3"><Badge tone={STATUS_TONE[r.status]}>{t(r.status)}</Badge></td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{r.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination page={page} count={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
              </Card>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminAttendance;
